import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { contactsService } from '../../services/contactsService';
import { purchasesService } from '../../services/purchasesService';
import vegetablesService from '../../services/vegetablesService';
import AddVegetableModal from '../sales/AddVegetableModal';
import AddContactModal from '../contacts/AddContactModal';
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { businessService } from '../../services/businessService';

export default function CreatePurchase({ isOpen, onClose, onPurchaseCreated }) {
  const [suppliers, setSuppliers] = useState([]);
  const [vegetables, setVegetables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddVegetable, setShowAddVegetable] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [businessDetails, setBusinessDetails] = useState(null);

  const [purchaseData, setPurchaseData] = useState({
    supplierId: '',
    items: [{ 
      vegetableId: '', 
      vegetable: '', 
      quantity: '',
      unit: 'kg',
      pricePerKg: '',
    }],
    discount: '',
    amountPaid: '',
    notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      const [suppliersData, vegetablesData, businessData] = await Promise.all([
        contactsService.getContacts('supplier'),
        vegetablesService.getVegetables(),
        businessService.getBusinessDetails()
      ]);
      setSuppliers(suppliersData);
      setVegetables(vegetablesData);
      setBusinessDetails(businessData);
    };
    fetchData();
  }, []);

  const handleAddItem = () => {
    setPurchaseData(prev => ({
      ...prev,
      items: [...prev.items, { vegetableId: '', vegetable: '', quantity: '', unit: 'kg', pricePerKg: '' }]
    }));
  };

  const handleItemChange = (index, field, value) => {
    if (field === 'vegetableId') {
      const vegetable = vegetables.find(v => v.id === value);
      const newItems = [...purchaseData.items];
      newItems[index] = {
        ...newItems[index],
        vegetableId: value,
        vegetable: vegetable.name
      };
      setPurchaseData(prev => ({ ...prev, items: newItems }));
    } else if (field === 'quantity' || field === 'pricePerKg') {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        const newItems = [...purchaseData.items];
        newItems[index] = { ...newItems[index], [field]: value };
        setPurchaseData(prev => ({ ...prev, items: newItems }));
      }
    }
  };

  const handleNumericChange = (field, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPurchaseData(prev => ({ ...prev, [field]: value }));
    }
  };

  const removeItem = (index) => {
    setPurchaseData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateSubtotal = () => {
    return purchaseData.items.reduce((total, item) => {
      const quantity = Number(item.quantity) || 0;
      const price = Number(item.pricePerKg) || 0;
      const quantityInKg = item.unit === 'box' 
        ? quantity * (businessDetails?.quantityPerBox || 25)
        : quantity;
      return total + (quantityInKg * price);
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = Number(purchaseData.discount) || 0;
    return subtotal - discount;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const hasValidItems = purchaseData.items.some(item => 
      item.vegetableId && item.quantity && item.pricePerKg
    );
    if (!hasValidItems) {
      alert('Please add at least one vegetable with quantity and price');
      return;
    }

    setLoading(true);
    try {
      const subtotal = calculateSubtotal();
      const discount = Number(purchaseData.discount) || 0;
      const totalAmount = subtotal - discount;
      const amountPaid = Number(purchaseData.amountPaid) || 0;
      const remainingBalance = totalAmount - amountPaid;
      
      // Process items to store both original and kg quantities
      const processedItems = purchaseData.items.map(item => {
        if (item.unit === 'box') {
          const quantityPerBox = businessDetails?.quantityPerBox || 25;
          return {
            ...item,
            quantityInKg: Number(item.quantity) * quantityPerBox,
            originalQuantity: item.quantity,
            originalUnit: 'box'
          };
        }
        return {
          ...item,
          quantityInKg: Number(item.quantity),
          originalQuantity: item.quantity,
          originalUnit: 'kg'
        };
      });

      await purchasesService.createPurchase({
        ...purchaseData,
        items: processedItems,
        totalAmount,
        remainingBalance,
        subtotal,
        status: 'completed',
        date: new Date()
      });

      if (onPurchaseCreated) {
        onPurchaseCreated();
      }
      onClose();
    } catch (error) {
      console.error('Error creating purchase:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full transform overflow-hidden bg-white dark:bg-gray-800 
                p-4 sm:p-6 text-left align-middle shadow-xl transition-all rounded-t-2xl sm:rounded-2xl
                sm:max-w-3xl">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Create Purchase
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Supplier Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Supplier
                    </label>
                    <div className="flex space-x-2">
                      <select
                        value={purchaseData.supplierId}
                        onChange={(e) => setPurchaseData(prev => ({ ...prev, supplierId: e.target.value }))}
                        className="flex-1 px-3 py-2 text-base rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        required
                      >
                        <option value="">Select Supplier</option>
                        {suppliers.map(supplier => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowAddSupplier(true)}
                        className="px-2 py-2 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                      >
                        <PlusIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="space-y-4">
                    {purchaseData.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-1 gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Vegetable
                            </label>
                            <div className="flex space-x-2">
                              <select
                                value={item.vegetableId}
                                onChange={(e) => handleItemChange(index, 'vegetableId', e.target.value)}
                                className="flex-1 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                required
                              >
                                <option value="">Select Vegetable</option>
                                {vegetables.map(veg => (
                                  <option key={veg.id} value={veg.id}>
                                    {veg.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => setShowAddVegetable(true)}
                                className="px-2 py-2 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                              >
                                <PlusIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Quantity
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                required
                              />
                              <select
                                value={item.unit}
                                onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                              >
                                <option value="kg">KG</option>
                                <option value="box">Box</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Price/kg
                            </label>
                            <input
                              type="text"
                              value={item.pricePerKg}
                              onChange={(e) => handleItemChange(index, 'pricePerKg', e.target.value)}
                              className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                              required
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="text-lg font-medium dark:text-white">
                            Amount: ₹{((Number(item.quantity) || 0) * (Number(item.pricePerKg) || 0)).toFixed(2)}
                          </p>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="w-full p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg 
                      text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 
                      flex items-center justify-center"
                  >
                    <PlusIcon className="h-6 w-6 mr-2" />
                    Add Item
                  </button>

                  {/* Totals Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="text-lg font-medium text-gray-700 dark:text-gray-300">Subtotal:</p>
                      <p className="text-xl font-medium dark:text-white">₹{calculateSubtotal().toFixed(2)}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Discount Amount
                        </label>
                        <input
                          type="text"
                          value={purchaseData.discount}
                          onChange={(e) => handleNumericChange('discount', e.target.value)}
                          className="w-full px-4 py-3 text-lg rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Amount Paid
                        </label>
                        <input
                          type="text"
                          value={purchaseData.amountPaid}
                          onChange={(e) => handleNumericChange('amountPaid', e.target.value)}
                          className="w-full px-4 py-3 text-lg rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-lg font-medium">
                      <p className="text-gray-700 dark:text-gray-300">Total After Discount:</p>
                      <p className="text-xl font-bold text-primary-600 dark:text-primary-400">
                        ₹{calculateTotal().toFixed(2)}
                      </p>
                    </div>

                    <div className="flex justify-between items-center text-lg font-medium">
                      <p className="text-gray-700 dark:text-gray-300">Remaining Balance:</p>
                      <p className="text-xl font-bold text-red-600 dark:text-red-400">
                        ₹{(calculateTotal() - (Number(purchaseData.amountPaid) || 0)).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={purchaseData.notes}
                      onChange={(e) => setPurchaseData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-gray-700 dark:text-gray-300 
                        bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg
                        hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white 
                        bg-primary-600 rounded-lg border border-transparent
                        hover:bg-primary-700 
                        focus:outline-none focus:ring-2 focus:ring-primary-500
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Creating...' : 'Create Purchase'}
                    </button>
                  </div>
                </form>

                <AddVegetableModal
                  isOpen={showAddVegetable}
                  onClose={() => setShowAddVegetable(false)}
                  onVegetableAdded={(newVegetable) => {
                    setVegetables(prev => [...prev, newVegetable]);
                    setShowAddVegetable(false);
                  }}
                />

                <AddContactModal
                  isOpen={showAddSupplier}
                  onClose={() => setShowAddSupplier(false)}
                  contactType="Supplier"
                  onContactAdded={(newSupplier) => {
                    setSuppliers(prev => [...prev, newSupplier]);
                    setShowAddSupplier(false);
                  }}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}